import type { Key } from 'react';
import { isArray } from 'lodash';

import {
  AuditEntityData,
  AuditPayload,
  AuditTrialType,
  ScoringDetails,
  VerificationSteps,
  Visits,
} from '@dynamika-shared/types/auditTrial';

import {
  AuditDetails,
  Detail,
  DetailValue,
  Info,
  QuestionnaireResponse,
  ScoringElement,
  VerificationStep,
  Visit,
} from '../components/types';
import {
  parseJSONOrReturnData,
  removeFieldsByEventName,
} from 'src/utils/auditFiltersHelpers';

export const AUDIT_VERSION_V3 = 3;

export const NESTED_PAYLOAD_EVENT_LIST = new Set([
  'Image Series Split',
  'Advanced Image Series Split',
  'Laterality Correction',
  'MPR Series',
]);

export const NESTED_PAYLOAD_BUSINESS_PROCESS_LIST = new Set([
  'NOTIFICATIONS_MANAGEMENT',
]);

export const hasNestedPayload = (info: Partial<AuditTrialType>): boolean => {
  if (NESTED_PAYLOAD_EVENT_LIST.has(info.event as string)) {
    return true;
  }

  const isEntityPayload = info.payloads?.[0]?.type === 'entity';
  const isV3 = info.auditVersion === AUDIT_VERSION_V3;

  return (
    !isV3 &&
    isEntityPayload &&
    NESTED_PAYLOAD_BUSINESS_PROCESS_LIST.has(info.businessProcess as string)
  );
};

export const skippedFields = new Set([
  'downloadScreenshotUrl',
  'downloadScreenshotThumbnailUrl',
  'measurementId',
]);
export const shouldBeFirst = {
  subjectId: true,
  comment: true,
  imageQuality: true,
  subOptimalQualityReason: true,
  subOptimalQualityOtherReason: true,
};

export const labels = {
  dateAndTime: 'Date & Time',
  actionBy: 'Action By',
  actionByEmail: 'Email',
  subjectId: 'Subject ID',
  subjectCode: 'Subject Code',
  visit: 'Visit',
  businessProcess: 'Business Process',
  event: 'Event',
  actionType: 'Action Type',
  field: 'Field',
  oldValue: 'Old Value',
  newValue: 'New Value',
  reasonForModification: 'Reason For Modification',
  dxaVisitData: 'DXA Visit Data',
  readingCode: 'Reading ID',
};

// NOTE: here is configuration of available fields.
// Add or remove if needed
export const fieldsToRemove: { [key: string]: boolean } = {
  id: true,
  siteId: true,
  cohortId: true,
  clinicalTrialId: true,
  patientId: true,
  patientCode: true,
  uploadAssets: true,
  visitId: true,
  studies: true,
};

export const fieldsToRemoveRelatedToDXA = {
  'DXA Result Reading Selection': ['id', 'labelId', 'label'],
};

export const changeOrderForCertainElement = (info: Info | null) => {
  if (!info) return [];
  const infoData = Object.entries(info);

  const firstFields: typeof infoData = [];

  const restFields = infoData.reduce(
    (acc, curr) => {
      const [firstVal, secondVal] = curr;

      if (shouldBeFirst[firstVal as keyof typeof shouldBeFirst] && secondVal) {
        firstFields.push([firstVal, secondVal]);
      } else {
        acc.push([firstVal, secondVal]);
      }

      return acc;
    },
    [] as typeof infoData
  );

  return [...firstFields, ...restFields];
};

export const convertScoringToArray = (
  scoring: ScoringDetails | null
): [] | ScoringElement[] => {
  if (!scoring) return [];
  const convertToArray = changeOrderForCertainElement(scoring);
  const updateToFlatStructure = convertToArray?.map(([key, val]) => ({
    [key]: val,
  }));
  return updateToFlatStructure as ScoringElement[];
};

export const determineFieldNameOrSeries = (
  fieldName: string,
  businessProcess: string
) => {
  const dxaBusinessProcesses = [
    'DXA Result Reading Selection',
    'DXA Result Sync',
  ];
  return dxaBusinessProcesses.includes(businessProcess) ? 'Field' : fieldName;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !isArray(value);

const renderableDetailValue = (stepValue: unknown): DetailValue =>
  isPlainObject(stepValue)
    ? ([stepValue] as DetailValue)
    : (stepValue as DetailValue);

const defaultDetailValue = (stepValue: unknown): DetailValue =>
  isArray(stepValue) ? 'n/a' : renderableDetailValue(stepValue ?? 'n/a');

// NOTE: Functions update details that located into a payloads.
export const createDetails = (
  payloadContent: AuditPayload | AuditEntityData,
  info: Partial<AuditTrialType>
) => {
  // Convert data object to array for further modification
  const payloadContentCheck = Object.entries(payloadContent) ?? [];

  const localFieldsToRemove = { ...fieldsToRemove };

  // Depending on field names - update the field
  return payloadContentCheck.reduce((prev, curr) => {
    const [stepType, stepValue] = curr;
    let detailValue: DetailValue;
    let detailKey = stepType;

    switch (stepType) {
      case 'verificationSteps':
        detailValue = stepValue.map((el: VerificationSteps) => ({
          title: el.title,
          status: el.status,
          comment: el.comment,
        }));
        break;
      case 'scoring':
        detailValue = convertScoringToArray(stepValue);
        break;
      case 'executionPayload':
        detailValue = convertScoringToArray(stepValue as ScoringDetails);
        break;
      case 'visits':
        detailValue = stepValue.map((el: Visits) => ({
          visitName: el.visitName,
        }));
        break;
      case 'questionnaireResponse':
        if (stepValue?.response) {
          detailValue = stepValue.modalitySelection
            ? [
                {
                  ...stepValue.response,
                  modalitySelection: stepValue.modalitySelection,
                },
              ]
            : [stepValue.response];
        } else {
          detailValue = 'n/a';
        }
        break;
      case 'response':
        if (stepValue) {
          detailValue = [stepValue];
        } else {
          detailValue = 'n/a';
        }
        break;
      case 'oldValue':
        detailValue = renderableDetailValue(stepValue);
        localFieldsToRemove.oldValue = !stepValue;
        break;
      case 'dicomEntities':
        detailValue = renderableDetailValue(stepValue);
        detailKey = 'Images/Series';
        break;
      case 'newValue':
        detailValue = renderableDetailValue(stepValue);
        localFieldsToRemove.newValue = !stepValue;
        break;
      case 'attachments':
        if (stepValue?.length) {
          detailValue = stepValue;
        } else {
          localFieldsToRemove.attachments = true;
        }
        break;
      default:
        detailValue = defaultDetailValue(stepValue);
        break;
    }

    if (
      stepType === 'fieldName' &&
      !['DXA Upload to Workstation', 'Data QC'].includes(
        info?.businessProcess as string
      )
    ) {
      detailKey = determineFieldNameOrSeries(
        'SeriesID',
        info?.businessProcess ?? ''
      );
      detailValue = renderableDetailValue(stepValue);
    } else {
      localFieldsToRemove.fieldName = true;
    }

    // Remove fields that were determinate as useless
    if (!localFieldsToRemove[detailKey]) {
      prev.push({ key: detailKey, value: detailValue, label: '' });
    }

    // Handle entity name field
    if (info?.payloads?.[0].entityName) {
      const fieldExist = prev.find(infoField => infoField.key === 'field');
      if (!fieldExist) {
        prev.push({
          key: 'field',
          value: info?.payloads?.[0].entityName,
          label: '',
        });
      }
    }

    // Handle entity data field -> series
    if (info?.payloads?.[0].entityData) {
      (info?.payloads?.[0]?.entityData?.studies?.[0].series ?? []).forEach(
        serie =>
          prev.push({
            key: 'seriesInstanceUid',
            value: serie.seriesInstanceUid,
            label: '',
          })
      );
    }

    return prev;
  }, [] as Detail[]);
};

export const modifyPayload = (
  payload: AuditPayload[],
  info: Partial<AuditTrialType>
) => {
  if (!payload.length) return [];

  if (payload[0].entityData) {
    return createDetails(payload[0].entityData, info);
  } else {
    return createDetails(payload[0], info);
  }
};

// NOTE: for v3 audit rows the envelope (date/time/actor/etc.) is hidden —
// only the `details` payload is surfaced. Includes `payloads` because the
// non-v3 payload-derived rows (SeriesID, type, …) are also envelope noise
// for v3.
const V3_HIDDEN_INFO_KEYS = new Set([
  'dateAndTime',
  'actionByName',
  'actionByEmail',
  'reasonForModification',
  'attributes',
  'payloads',
]);

// NOTE: prepare data before rendering
export const detailsPreparation = (info: Partial<AuditTrialType>) => {
  // simplify data structure for further looping
  const infoData = changeOrderForCertainElement(info);
  const isV3 = info.auditVersion === AUDIT_VERSION_V3;

  // conditionaly change the field and push it to new array.
  // Return updated array.
  return infoData.reduce((prev, curr) => {
    const [infoKey, infoValue] = curr;

    if (isV3 && V3_HIDDEN_INFO_KEYS.has(infoKey)) {
      return prev;
    }

    if (infoKey === 'actionByEmail') {
      prev.push({
        key: 'actionByEmail',
        label: labels['actionByEmail'],
        value: info.actionByEmail,
      });
    } else if (infoKey === 'actionByName') {
      prev.push({
        key: 'actionBy',
        label: labels['actionBy'],
        value: info.actionByName,
      });
    } else if (infoKey === 'executionPayload') {
      prev.push({
        key: 'executionPayload',
        label: info.attributes?.subjectCode as string,
        value: `${info.attributes?.subjectCode}`,
      });
    } else if (infoKey === 'subjectID') {
      prev.push({
        key: 'subjectID',
        label: info.attributes?.subjectCode as string,
        value: `${info.attributes?.subjectCode}`,
      });
    } else if (infoKey === 'dateAndTime') {
      const [from, to] = (infoValue as string).split(' ');
      prev.push({
        key: 'date',
        label: labels[infoKey as keyof AuditDetails],
        value: <span>{from}</span>,
      });
      prev.push({
        key: 'time',
        label: labels[infoKey as keyof AuditDetails],
        value: <span>{to}</span>,
      });
    } else if (infoKey === 'dxaVisitData' && infoValue) {
      const checkAndPrepareData = parseJSONOrReturnData(infoValue);
      const dxaDataFromJSON = infoValue ? checkAndPrepareData : 'N/A';
      prev.push({
        key: 'dxaVisitData',
        label: labels[infoKey as keyof AuditDetails],
        value: dxaDataFromJSON,
      });
    } else if (infoKey === 'attributes' && info?.attributes?.visitName) {
      prev.push({
        key: 'visitName',
        label: info?.attributes?.visitName,
        value: `${info?.attributes?.visitName}`,
      });
      if (info?.attributes?.readingCode) {
        prev.push({
          key: labels['readingCode'],
          label: info?.attributes?.readingCode,
          value: `${info?.attributes?.readingCode}`,
        });
      }
    } else if (infoKey === 'reasonForModification' && infoValue) {
      prev.push({
        key: 'reasonForModification',
        label: 'Reason For Modification',
        value: infoValue as DetailValue,
      });
    } else if (infoKey === 'payloads') {
      // NOTE: this section is focused on payloads depends on the content
      const payload = modifyPayload(infoValue as AuditPayload[], info);

      prev = [...prev, ...payload];
    } else if (
      infoKey === 'details' &&
      info.auditVersion === AUDIT_VERSION_V3 &&
      infoValue &&
      typeof infoValue === 'object'
    ) {
      Object.entries(infoValue as Record<string, unknown>).forEach(
        ([key, value]) => {
          if (Array.isArray(value)) {
            prev.push({
              key,
              label: '',
              value: value.map((item, idx) => {
                const itemKey = `${key}-${idx}`;
                const cleaned = removeSkippedFields(item);
                if (typeof cleaned !== 'object' || cleaned === null) {
                  return (
                    <div key={itemKey}>
                      {cleaned == null ? 'n/a' : String(cleaned)}
                    </div>
                  );
                }
                return renderJsonBlock(cleaned, itemKey);
              }),
            });
          } else if (typeof value === 'object' && value !== null) {
            const cleaned = removeSkippedFields(value) as object;
            prev.push({
              key,
              label: '',
              value: [renderJsonBlock(cleaned, key)],
            });
          } else {
            prev.push({
              key,
              label: '',
              value: value == null ? 'n/a' : String(value),
            });
          }
        }
      );
    }

    return prev;
  }, [] as Detail[]);
};

// NOTE: function accept an child element - object and recursively transform it
export const jsonChildren = (
  obj: VerificationStep | Visit | QuestionnaireResponse | object
) => {
  const childDetailWithoutSomeFields = removeFieldsByEventName(
    obj,
    fieldsToRemoveRelatedToDXA,
    'DXA Result Reading Selection'
  );

  const childDetail = Object.entries(childDetailWithoutSomeFields);

  if (childDetail.length === 0) return null;

  return childDetail
    .map(([detailKey, detailValue]) => {
      const detailValueIsNotNullish = detailValue ?? false;

      if (detailValue && typeof detailValue === 'object') {
        const subChild = jsonChildren(detailValue);
        if (!subChild) return null;

        return (
          <div key={detailKey} className='pl-[2rem]'>
            <span className='text-[#1C1C1C]'>{`"${detailKey}"`}: </span>
            {'{'}&nbsp;{'\n'}
            {subChild}
            {'\n'}
            {'}'} ,
          </div>
        );
      }

      if (
        typeof detailValue === 'boolean' ||
        (detailValueIsNotNullish &&
          (typeof detailValue === 'string' || typeof detailValue === 'number'))
      ) {
        return (
          <div key={detailKey} className='pl-[2rem]'>
            <span className='text-[#1C1C1C]'>{`"${detailKey}"`}: </span>
            <span>{`"${detailValue}"`}</span>
          </div>
        );
      }

      return null;
    })
    .filter(Boolean);
};

export const renderJsonBlock = (cleaned: object, key: Key) => (
  <div className='ml-[4rem]' key={key}>
    {'{'}&nbsp;{'\n'}
    {jsonChildren(cleaned)}
    {'\n'}
    {'}'} ,
  </div>
);

export function removeSkippedFields<T>(input: T): T {
  if (Array.isArray(input)) {
    return input.map(removeSkippedFields) as unknown as T;
  }

  if (typeof input === 'object' && input !== null) {
    return Object.fromEntries(
      Object.entries(input)
        .filter(([key]) => !skippedFields.has(key))
        .map(([key, value]) => [key, removeSkippedFields(value)])
    ) as T;
  }

  return input;
}

export const payloadsPreparation = (payloads?: AuditPayload[]): Detail[] => {
  if (!Array.isArray(payloads) || payloads.length === 0) return [];

  const details: Detail[] = [];

  payloads.forEach((payload, index) => {
    const { entityName, entityData } = payload;
    if (entityName) {
      details.push({
        key: `entityName-${index}`,
        label: '',
        value: entityName,
      });
    }

    if (entityData && typeof entityData === 'object') {
      const flattened = flattenPayloadObject(entityData);

      flattened.forEach(({ key, value }, idx) => {
        if (
          typeof value === 'object' &&
          value !== null &&
          Object.values(value).filter(Boolean).length > 0
        ) {
          const cleaned = removeSkippedFields(value);
          details.push({
            key,
            label: '',
            value: [renderJsonBlock(cleaned, idx)],
          });
        } else {
          details.push({
            key,
            label: '',
            value: String(value ?? 'n/a'),
          });
        }
      });
    }
  });

  return details;
};

function flattenPayloadObject(
  obj: Record<string, unknown>
): { key: string; value: unknown }[] {
  return Object.entries(obj).flatMap(([key, value]) => {
    const newKey = key;

    if (Array.isArray(value)) {
      return value.flatMap((item, idx) =>
        typeof item === 'object' && item !== null
          ? flattenPayloadObject(item)
          : [{ key: `${newKey}[${idx}]`, value: item }]
      );
    }

    if (typeof value === 'object' && value !== null) {
      return flattenPayloadObject(value as Record<string, unknown>);
    }

    return [{ key: newKey, value }];
  });
}
